<template>
    <div class="linked-offers-list">
        <div class="row mt-3 justify-content-center">
            <div class="col-lg-9 col-xl-6 d-flex">
                <div class="col-form-label" style="flex-grow: 1">
                    <h5 class="kt-section__title kt-section__title-sm mb-2">
                        {{ translations.linkOffer.linkedOffers }}
                    </h5>
                </div>
                <div class="mb-4">
                    <button @click="onClickAddRow" type="button" class="btn add-linked-offer pull-right btn-success">
                        <i class="la la-plus"></i>
                        {{ translations.linkOffer.linkedOffer }}
                    </button>
                </div>
            </div>
        </div>
        <div class="linked-offers-container d-flex flex-column">
            <div class="loading-container" v-if="isLoadingOffers">
                <i class="fas fa-spinner fa-spin"></i>
            </div>
            <div v-if="linkedOffers.length === 0" class="no-linked-offers-text">
                {{ translations.linkOffer.noLinkedOffers }}
            </div>
            <div v-if="linkedOffers.length > 0" v-for="linkedOffer in linkedOffers" :key="linkedOffer.id">
                <LinkedOfferRow @on-click-remove-row="onClickRemoveRow" :linked-offer="linkedOffer" />
            </div>
        </div>
    </div>
    <div class="row mt-4 justify-content-center">
        <div class="col-lg-9 col-xl-6 d-flex justify-content-end">
            <button @click="onClickApply" type="button" class="btn btn-success">
                <i class="la la-floppy-o"></i>
                {{ translations.linkOffer.saveAndResetSorting }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { ILinkedOfferToUpdate } from "@/data/commands/catalog/CreateOfferCmd";
    import { Factory } from "@/data/dao/Factory";
    import { CatalogDao } from "@/data/dao/service/CatalogDao";
    import type { ILinkedOfferEntity, ILinkedOffers } from "@/data/model/Location";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { v7 as uuidv7 } from "uuid";
    import { onMounted, ref } from "vue";

    import LinkedOfferRow from "@/portal/components/layout/offer/LinkedOfferRow.vue";

    const catalogDao = Factory.getInstance<CatalogDao>(CatalogDao);
    const linkedOffers = ref<ILinkedOffers[]>([]);
    const isLoadingOffers = ref<boolean>(false);
    const isApplyingChanges = ref<boolean>(false);

    // define props
    const props = defineProps<{
        parentOfferId: string;
        onErrorOccured?: (message: string | undefined) => void;
        onChangesApplied?: () => void;
    }>();

    onMounted(async () => {
        // Load parent offer
        try {
            isLoadingOffers.value = true;
            const offer = await catalogDao.getOffer(props.parentOfferId);
            linkedOffers.value = convertToLinkedOffers(offer.linkedOffers);
        } catch (error) {
            logError(error);
            if (props.onErrorOccured) {
                props.onErrorOccured(translations.general.failedLoadingData);
            }
        } finally {
            isLoadingOffers.value = false;
        }
    });

    function onClickAddRow() {
        linkedOffers.value.push({
            id: uuidv7(),
            parentOfferId: props.parentOfferId,
            linkedOfferIds: [],
            linkedLocationId: undefined,
        });
    }

    function onClickRemoveRow(id: string) {
        linkedOffers.value = linkedOffers.value.filter((lo) => lo.id !== id);
    }

    async function onClickApply() {
        let linkedOffersToUpdate = new Array<ILinkedOfferToUpdate>();

        for (let linkedOffer of linkedOffers.value.filter(
            (l) => l.linkedLocationId != null && l.linkedOfferIds.length > 0
        )) {
            linkedOffersToUpdate.push({
                linkedLocationId: linkedOffer.linkedLocationId,
                offerIds: linkedOffer.linkedOfferIds,
            });
        }

        try {
            isApplyingChanges.value = true;

            const updatedLinkedOffers = await catalogDao.updateLinkedOffers(props.parentOfferId, {
                linkedOffersToUpdate: linkedOffersToUpdate,
            });

            linkedOffers.value = convertToLinkedOffers(updatedLinkedOffers);

            if (props.onChangesApplied) {
                props.onChangesApplied();
            }
        } catch (error) {
            logError(error);
            if (props.onErrorOccured) {
                props.onErrorOccured(translations.general.failedLoadingData);
            }
        } finally {
            isApplyingChanges.value = false;
        }
    }

    function convertToLinkedOffers(linkedOffers: Array<ILinkedOfferEntity>): Array<ILinkedOffers> {
        let currentLinkedOffers = new Array<ILinkedOffers>();

        for (let row of linkedOffers) {
            let linkedOffersRow = currentLinkedOffers.find((l) => l.linkedLocationId === row.linkedLocationId);
            if (linkedOffersRow != null) {
                linkedOffersRow.linkedOfferIds.push(row.offerId);
            } else {
                currentLinkedOffers.push({
                    id: uuidv7(),
                    linkedLocationId: row.linkedLocationId,
                    parentOfferId: row.parentOfferId,
                    linkedOfferIds: [row.offerId],
                });
            }
        }

        return currentLinkedOffers;
    }
</script>

<style scoped lang="scss">
    @use "./../../../../../assets/styles/scss/shared/colors.scss";

    .linked-offers-list {
        .linked-offers-container {
 

            .no-linked-offers-text {
                font-size: 1.3rem;
                font-weight: 500;
                color: #48465b;
                text-align: center;
                margin: 1rem 0;
            }

            .loading-container {
                font-size: 2rem;
                text-align: center;
                color: colors.$greenHover;
            }
        }
    }
</style>
